import { DateTime } from 'luxon';
import { List as ImList, Map as ImMap } from 'immutable';

import { getLogger } from 'companion-app-components/utils/core';
import { CashFlowTransaction, statusOrder } from 'companion-app-components/flux/transactions/transactionsTypes';
import { chartOfAccountsSelectors } from 'companion-app-components/flux/chart-of-accounts';

import { transactionsSortFunction } from 'data/transactions/utils';

const log = getLogger('data/transactionList/selectors');

function compareDate(txn1: CashFlowTransaction, txn2: CashFlowTransaction) {
  const txn1Date = DateTime.fromISO(txn1.postedOn);
  const txn2Date = DateTime.fromISO(txn2.postedOn);
  if (txn1Date < txn2Date) {
    return -1;
  }
  if (txn1Date > txn2Date) {
    return 1;
  }
  return 0;
}

export function sortTransactions(
  txns: ImMap<string, CashFlowTransaction>,
  optSortBy: string,
  optSortOrder: string,
  featureFlags?: ImMap<string, any>,
) {
  const sortBy: string = optSortBy !== undefined ? optSortBy : 'date';
  const sortOrder: string = optSortOrder !== undefined ? optSortOrder : 'descending';

  log.log('In selector performing SORT', sortBy, sortOrder);
  const sortFactor = sortOrder === 'ascending' ? 1 : -1;

  const sortedTxns = ImList(txns.toList()).sort(
    (txn1: CashFlowTransaction, txn2: CashFlowTransaction) => {
      let cmpValue = 0;
      switch (sortBy) {
        case 'payee':
          cmpValue = 1;
          if (txn1.payee === txn2.payee) {
            cmpValue = compareDate(txn1, txn2);
            // sometimes payee can be null
          } else if ((txn1.payee || '')?.toLowerCase() < (txn2.payee || '')?.toLowerCase()) {
            cmpValue = -1;
          }
          break;
        case 'check': {
          cmpValue = 1;

          const aChecknum = (txn1.check && txn1.check.number) || '';
          const bChecknum = (txn2.check && txn2.check.number) || '';

          if (aChecknum === bChecknum) {
            cmpValue = compareDate(txn1, txn2);
          } else if (Number.isNaN(Number(aChecknum)) || Number.isNaN(Number(bChecknum))) {
            cmpValue = aChecknum?.toLowerCase() < bChecknum?.toLowerCase() ? -1 : 1;
          } else {
            cmpValue = Number(bChecknum) - Number(aChecknum);
            // cmpValue = -1;
          }
          break;
        }
        case 'amount':
          cmpValue = 1;

          if (Number(txn1.amount) === Number(txn2.amount)) {
            cmpValue = compareDate(txn1, txn2);
          } else if (Number(txn1.amount) < Number(txn2.amount)) {
            cmpValue = -1;
          }
          break;
        case 'category': {
          const strA = txn1.coa ? chartOfAccountsSelectors.getCoaStringSelector(undefined, txn1.coa, true) : '';
          const strB = txn2.coa ? chartOfAccountsSelectors.getCoaStringSelector(undefined, txn2.coa, true) : '';
          cmpValue = 1;

          if (strA === strB) {
            cmpValue = compareDate(txn1, txn2);
          } else if (strA?.toLowerCase() < strB?.toLowerCase()) {
            cmpValue = -1;
          }
          break;
        }

        case 'reviewed':
        case 'date':
          if (featureFlags && !featureFlags.get('labelPendingAsFuture')) {
            if (txn1.state === 'PENDING' || txn2.state === 'PENDING') {
              if (txn1.state === txn2.state) {
                cmpValue = 0;
              } else {
                cmpValue = txn1.state === 'PENDING' ? 1 : -1;
              }
            }
          }
          if (cmpValue === 0) {
            cmpValue = transactionsSortFunction(txn1, txn2);
          }
          break;
        case 'status':
          cmpValue = statusOrder.indexOf(txn1.state!) - statusOrder.indexOf(txn2.state!);
          if (cmpValue === 0) {
            // for cash-flow uses, secondary-sort needs to be on date
            cmpValue = compareDate(txn1, txn2);
          }
          break;
        default:
          break;
      }

      // if all else equal, sort by ID to guarantee repeatable sorting
      if (cmpValue === 0) {
        // @ts-expect-error - FIXME: txn1.id & txn2.id is possibly undefined
        cmpValue = txn1.id - txn2.id;
      }

      return cmpValue * sortFactor;
    },
  );

  return sortedTxns;
}
